import { WebsocketMessage, WebsocketReloadData, WS_NS_RELOAD } from "@dto/websocketMessage.dto";
import { useEffect, useState } from "react";
import useWebSocketSingleton from "./useWebSocketSingleton";

/** `resync` marks a reload the client synthesised after a reconnect, not someone else's change. */
export interface ReloadMessage {
  event: 'reload';
  data: WebsocketReloadData & { resync?: boolean; };
}

function isReload(ws: WebsocketMessage): ws is WebsocketMessage<'reload'> {
  return ws?.event === 'reload';
}

function isLicense(ws: WebsocketMessage): ws is WebsocketMessage<'license'> {
  return ws?.event === 'license';
}

function isGrafana(ws: WebsocketMessage): ws is WebsocketMessage<'grafana'> {
  return ws?.event === 'grafana';
}

function isLLM(ws: WebsocketMessage): ws is WebsocketMessage<'llm'> {
  return ws?.event === 'llm';
}

export const useWebSocketGrafana = () => {
  const { lastByEvent } = useWebSocketSingleton(WS_NS_RELOAD);
  const grafana = lastByEvent.grafana;
  return isGrafana(grafana) ? grafana.data.active : undefined;
}

export const useWebSocketLLM = () => {
  const { lastByEvent } = useWebSocketSingleton(WS_NS_RELOAD);
  const llm = lastByEvent.llm;
  return isLLM(llm) ? llm.data.active : undefined;
}

export const useWebSocketLicense = () => {
  const { lastByEvent } = useWebSocketSingleton(WS_NS_RELOAD);
  const license = lastByEvent.license;
  return isLicense(license) ? license : undefined;
}

export const useWebSocketReload = (reloadType: WebsocketReloadData['type']) => {
  const [data, setData] = useState<ReloadMessage>();
  const { message: reload, reconnects } = useWebSocketSingleton(WS_NS_RELOAD);

  useEffect(() => {
    if (isReload(reload) && reload.data.type === reloadType)
      setData(reload);
  }, [reload, reloadType]);

  useEffect(() => {
    // 'session' means the server is going down and logs the user out — never synthesise it
    if (reconnects && reloadType !== 'session')
      setData({ event: 'reload', data: { type: reloadType, resync: true } });
  }, [reconnects, reloadType]);

  return data;
}

export const useWebSocketReloadGetRandomNumberOnReload = (reloadType: WebsocketReloadData['type']) => {
  const [data, setData] = useState<number>();
  const { message: reload, reconnects } = useWebSocketSingleton(WS_NS_RELOAD);

  useEffect(() => {
    if (isReload(reload) && reload.data.type === reloadType)
      setData(Math.random() * 1000);
  }, [reload, reloadType]);

  // reloads sent while the socket was down never arrived, refresh in case one was missed
  useEffect(() => {
    if (reconnects)
      setData(Math.random() * 1000);
  }, [reconnects]);

  return data;
}
